import React from 'react';
import { useForm } from 'react-hook-form';
import { Provider } from '../../types';
import Input from './Input';
import Select from './Select';
import TextArea from './TextArea';
import Button from './Button';

interface ProviderFormProps {
  provider?: Provider;
  onSubmit: (data: Partial<Provider>) => void;
  onCancel: () => void;
}

const ProviderForm: React.FC<ProviderFormProps> = ({ provider, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<Provider>({
    defaultValues: provider || {
      name: '',
      email: '',
      phone: '',
      address: '',
      contactPerson: '',
      status: 'active'
    }
  });

  const handleFormSubmit = (data: Provider) => {
    // Strapi rejects empty strings on some field types (e.g. email) - send null instead
    const cleaned: any = { ...data };
    (['contactPerson', 'email', 'phone', 'address'] as const).forEach((field) => {
      if (typeof cleaned[field] === 'string' && cleaned[field].trim() === '') {
        cleaned[field] = null;
      }
    });

    onSubmit({
      ...cleaned,
      ...(provider ? { id: provider.id, documentId: provider.documentId } : {}),
      createdAt: provider?.createdAt || new Date().toISOString()
    });
  };

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {provider ? 'Edit Provider' : 'Add New Provider'}
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Provider Name"
            {...register('name', { 
              required: 'Provider name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' }
            })}
            error={errors.name?.message}
            placeholder="Enter provider name"
          />

          <Input
            label="Contact Person"
            {...register('contactPerson')}
            error={errors.contactPerson?.message}
            placeholder="Enter contact person name"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Email"
            type="email"
            {...register('email', {
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            error={errors.email?.message}
            placeholder="provider@example.com"
          />

          <Input
            label="Phone"
            {...register('phone', {
              pattern: {
                value: /^[+]?[0-9\s\-()]{4,20}$/,
                message: 'Invalid phone number'
              }
            })}
            error={errors.phone?.message}
            placeholder="+1-555-0123"
          />
        </div>

        <TextArea
          label="Address"
          {...register('address')}
          error={errors.address?.message}
          placeholder="Enter complete address"
          rows={3}
        />

        <Select
          label="Status"
          {...register('status', { required: 'Status is required' })}
          options={statusOptions}
          error={errors.status?.message}
        />

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
          >
            {provider ? 'Update Provider' : 'Create Provider'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProviderForm;